import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// 다가오는 일정 알림 팝업
//  - 오늘 ~ 5일 후 사이의 중도금/잔금/임차만료 일정을 한 창에 모아 표시
//  - "오늘 하루 안 보기" 체크 시 localStorage에 날짜를 기록해 당일 재노출 차단
// ─────────────────────────────────────────────

const TYPE_INFO = {
  middle:  { color: '#2196F3', label: '중도금' },
  balance: { color: '#4CAF50', label: '잔금' },
  expiry:  { color: '#f44336', label: '임차만료' },
};

// 알림 대상 종류 (계약금 deposit 은 제외)
const ALERT_TYPES = ['middle', 'balance', 'expiry'];

const ymd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// 소재지 축약: "서울특별시 관악구 봉천동 14-7 104호" → "봉천동_14-7_104호"
const shortAddr = (full, dong) => {
  let name = String(full || '').trim();
  if (dong) {
    const i = name.indexOf(dong);
    if (i >= 0) name = name.slice(i);
  }
  return name.replace(/\s+/g, '_');
};

// 규칙(offset)을 적용해 문자 발송 예정일을 구한다
// 예: 잔금 3일 전 → 잔금일 - 3일
const calcSendDate = (ev) => {
  const base = new Date(String(ev.event_date).split('T')[0] + 'T00:00:00');
  const v = Number(ev.offset_value) || 0;
  const sign = ev.offset_dir === 'after' ? 1 : -1;

  switch (ev.offset_unit) {
    case 'month': base.setMonth(base.getMonth() + sign * v); break;
    case 'week':  base.setDate(base.getDate() + sign * v * 7); break;
    case 'day':   base.setDate(base.getDate() + sign * v); break;
    case 'hour':  break;   // 시간 단위는 날짜 기준으로 동일 취급
    default: break;
  }
  return base;
};

const STORAGE_KEY = 'scheduleAlert.hideUntil';

export default function ScheduleAlert() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [hideToday, setHideToday] = useState(false);

  // 드래그 이동용 상태
  //  pos    : 화면상 좌표(px)
  //  dragRef: 드래그 시작 시점의 마우스/창 좌표 차이를 보관
  const [pos, setPos] = useState({ x: null, y: 80 });
  const dragRef = useRef(null);

  // 마우스를 누른 지점과 창 좌상단의 차이를 기억해두고,
  // 이동 중에는 그 차이를 유지하도록 좌표를 갱신한다
  const onDragStart = (e) => {
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      setPos({ x: ev.clientX - dragRef.current.dx, y: ev.clientY - dragRef.current.dy });
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    const today = ymd(new Date());

    // 오늘 하루 안 보기가 설정돼 있으면 조회 자체를 하지 않는다
    if (localStorage.getItem(STORAGE_KEY) === today) return;

    // 로그인 상태가 아니면 알림 불필요
    if (!localStorage.getItem('token')) return;

    // 메인 페이지에서만 노출한다
    //  - 상세/목록/캘린더 등 하위 페이지로 이동할 때마다 뜨는 것을 방지
    if (window.location.pathname !== '/') return;

    const fetchUpcoming = async () => {
      try {
        // 규칙 offset이 최대 몇 개월 전일지 모르므로 넉넉히 1년 범위를 조회하고
        // 아래에서 '발송 예정일 -2일 ~ 일정 당일' 조건으로 걸러낸다
        const from = new Date();
        const to = new Date();
        to.setFullYear(to.getFullYear() + 1);

        const res = await axios.get('/api/contracts/schedules/all', {
          params: { from: ymd(from), to: ymd(to) },
        });

        if (res.data.success) {
          const today = new Date(ymd(new Date()) + 'T00:00:00');

          const list = res.data.data.filter((e) => {
            if (!ALERT_TYPES.includes(e.event_type)) return false;
            if (e.reminder_sent) return false;        // 이미 발송됨 → 노출 안 함
            if (!e.rule_enabled) return false;        // 규칙이 꺼진 종류 → 제외

            // 노출 구간: (발송 예정일 - 2일) ~ 일정 당일
            const sendAt = calcSendDate(e);
            const start = new Date(sendAt);
            start.setDate(start.getDate() - 2);
            const end = new Date(String(e.event_date).split('T')[0] + 'T00:00:00');

            return today >= start && today <= end;
          });

          if (list.length > 0) {
            setEvents(list);
            setOpen(true);
          }
        }
      } catch (e) {
        console.log('[ScheduleAlert] 일정 조회 실패');
      }
    };

    fetchUpcoming();
  }, []);

  const handleClose = () => {
    if (hideToday) {
      localStorage.setItem(STORAGE_KEY, ymd(new Date()));
    }
    setOpen(false);
  };

  if (!open || events.length === 0) return null;

  // 오늘까지 남은 일수 계산
  const daysLeft = (dateStr) => {
    const target = new Date(String(dateStr).split('T')[0]);
    const today = new Date(ymd(new Date()));
    return Math.round((target - today) / 86400000);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: pos.y,
        left: pos.x !== null ? pos.x : undefined,
        right: pos.x === null ? '24px' : undefined,   // 최초에는 우측 상단
        zIndex: 10000,
        backgroundColor: '#fff', borderRadius: '8px',
        width: '380px', maxHeight: '70vh',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        border: '1px solid #ddd',
        fontFamily: 'Arial, sans-serif',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* 제목 표시줄 — 여기를 잡고 드래그한다 */}
      <div
        onMouseDown={onDragStart}
        style={{
          padding: '12px 15px', cursor: 'move', userSelect: 'none',
          backgroundColor: '#f5f5f5', borderRadius: '8px 8px 0 0',
          borderBottom: '1px solid #eee',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <strong style={{ fontSize: '15px' }}>
          🔔 다가오는 일정 ({events.length}건)
        </strong>
        <button
          onClick={handleClose}
          style={{
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '16px', color: '#666', padding: '0 4px',
          }}
        >
          ✕
        </button>
      </div>

      {/* 본문 (스크롤 영역) */}
      <div style={{ padding: '15px', overflowY: 'auto', flex: 1 }}>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
          발송 예정일이 임박한 일정입니다. 클릭하면 계약서로 이동합니다.
        </div>

        {events.map((ev) => {
          const info = TYPE_INFO[ev.event_type] || {};
          const left = daysLeft(ev.event_date);
          return (
            <div
              key={ev.schedule_id}
              onClick={() => (window.location.href = `/contracts/${ev.contract_id}`)}
              style={{
                padding: '10px', marginBottom: '8px', borderRadius: '4px',
                backgroundColor: '#fafafa', cursor: 'pointer',
                borderLeft: `4px solid ${info.color || '#999'}`,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: '13px' }}>
                  {info.label}_{shortAddr(ev.location_full_address, ev.location_dong)}
                </strong>
                <span style={{
                  fontSize: '10px', color: '#2563eb', margin: '0 8px',
                  lineHeight: '1.2', textAlign: 'center', whiteSpace: 'pre-line',
                }}>
                  {'일정확인 및\n문자발송'}
                </span>
                <span style={{
                  fontSize: '11px', color: left === 0 ? '#f44336' : '#666',
                  fontWeight: left === 0 ? 'bold' : 'normal',
                }}>
                  {left === 0 ? '오늘' : `D-${left}`}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {String(ev.event_date).split('T')[0]}
              </div>
            </div>
          );
        })}

      </div>

      {/* 푸터 (고정) */}
      <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 15px', borderTop: '1px solid #eee', backgroundColor: '#fafafa',
          borderRadius: '0 0 8px 8px',
        }}>
          <label style={{ fontSize: '13px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hideToday}
              onChange={(e) => setHideToday(e.target.checked)}
              style={{ marginRight: '6px' }}
            />
            오늘 하루 안 보기
          </label>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: '#757575', color: 'white', border: 'none',
              padding: '8px 20px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px',
            }}
          >
            닫기
          </button>
      </div>
    </div>
  );
}
